import RightOutlined from "@ant-design/icons/RightOutlined";
import styled, { useTheme } from "styled-components";
import useSearchInput from "utils/hooks/useSearchInput";
import { ItemInfoProps, ItemsListProps, ListItem } from "common/Lists/types";
import React, { useContext } from "react";
import {
  ListInnerItemWrapper,
  ListItemNameAndIconWrapper,
  ListItemWrapper,
} from "common/Lists/styles";
import TextWithIcon from "common/Text/TextWithIcon";
import { checkSymbolSearch } from "@shared/utils/misc";
import { openMessage } from "utils/misc";
import FlatList from "common/Lists/FlatList.tsx";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const ItemsList = <T extends any>({
  items,
  loading,
  handleSelectItem,
  iconType,
  itemComponent: ItemComponent = ItemInfo,
  style,
}: ItemsListProps<T>) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isDrawer } = useContext(DrawerContext) || {};
  const [SearchInput, searchValue] = useSearchInput({
    placeholder: t("labels.search_placeholder"),
    style: { width: "100%", marginBottom: 12, marginTop: 4 },
    focusOnMount: true,
  });

  const filteredItems = items.filter((item) =>
    checkSymbolSearch(item.displayName, searchValue)
  );

  const renderContent = ({ item }: { item: ListItem<T> }) => (
    <ItemComponent
      handleSelectItem={handleSelectItem}
      iconType={iconType}
      item={item}
      style={style}
    />
  );

  return (
    <Wrapper style={style}>
      <SearchWrapper>{SearchInput}</SearchWrapper>
      <ListWrapper>
        <FlatList
          data={filteredItems}
          loading={loading}
          renderContent={renderContent}
          gap={0}
          wrapperStyle={{ paddingLeft: 0, paddingRight: 0 }}
          loadingProps={
            isDrawer ? undefined : { backgroundColor: theme.colors.cardBgColor }
          }
          paddingBottom={36}
        />
      </ListWrapper>
    </Wrapper>
  );
};

export default ItemsList;

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const SearchWrapper = styled.div`
  padding: 0 16px;
`;
const ListWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ItemInfo = <T extends any>({
  item,
  handleSelectItem,
  iconType,
  style,
}: ItemInfoProps<T>) => {
  const { displayName, getIconValue, selectHandlerItem, errorMessage } = item;

  const handleClick = () => {
    if (errorMessage) {
      openMessage(errorMessage, "warning");
    } else if (selectHandlerItem) {
      handleSelectItem?.(selectHandlerItem);
    }
  };

  return (
    <ListItemWrapper
      onClick={handleClick}
      style={style}
      $invalid={!!errorMessage}
    >
      <ListInnerItemWrapper>
        <ListItemNameAndIconWrapper>
          {iconType ? (
            <TextWithIcon
              text={displayName}
              coinOrExchangeName={getIconValue}
              iconType={iconType}
              iconSize={"l"}
            />
          ) : (
            displayName
          )}
        </ListItemNameAndIconWrapper>
        <RightOutlined />
      </ListInnerItemWrapper>
    </ListItemWrapper>
  );
};
